import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, 
  Download, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  AlertTriangle,
  FileText,
  MoreVertical,
  Trash2
} from 'lucide-react';
import { useRequiredDocuments } from '@/hooks/useRequiredDocuments';
import { usePartnerDocuments } from '@/hooks/usePartnerDocuments';
import { DocumentUploadDialog } from './DocumentUploadDialog';
import { DocumentVerificationDialog } from './DocumentVerificationDialog';
import { useUserRole } from '@/hooks/useUserRole';
import { formatFileSize } from '@/lib/formatFileSize';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface UnifiedDocumentListProps {
  companyId: string;
  isContractorView?: boolean;
  contractorUserId?: string;
}

export function UnifiedDocumentList({ 
  companyId, 
  isContractorView = false,
  contractorUserId 
}: UnifiedDocumentListProps) {
  const { isTenantAdmin } = useUserRole();
  const { requiredDocuments, stats, isLoading: isLoadingRequired } = useRequiredDocuments(companyId);
  const { 
    documents, 
    isLoading: isLoadingDocs,
    downloadDocument,
    verifyDocument,
    deleteDocument 
  } = usePartnerDocuments(companyId);

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [preselectedDocType, setPreselectedDocType] = useState<string | undefined>();
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<any>(null);

  const isLoading = isLoadingRequired || isLoadingDocs;

  // Merge required documents with uploaded documents
  const unifiedDocuments = useMemo(() => {
    return requiredDocuments.map(reqDoc => {
      const uploadedDoc = documents?.find(doc => doc.document_type === reqDoc.documentType.name);
      return {
        ...reqDoc,
        uploadedDocument: uploadedDoc,
      };
    });
  }, [requiredDocuments, documents]);

  const handleUpload = (documentType?: string) => {
    setPreselectedDocType(documentType);
    setUploadDialogOpen(true);
  };

  const handleVerify = (doc: any) => {
    setSelectedDoc(doc);
    setVerifyDialogOpen(true);
  };

  const handleDownload = async (doc: any) => {
    try {
      await downloadDocument(doc.file_path);
    } catch (error) {
      // Error toast handled by the mutation
    }
  };

  const handleDeleteClick = (doc: any) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (docToDelete) {
      try {
        await deleteDocument(docToDelete.id);
        setDeleteDialogOpen(false);
        setDocToDelete(null);
      } catch (error) {
        // Error toast handled by the mutation
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'expired':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'expiring_soon':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      default:
        return <FileText className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', label: string }> = {
      verified: { variant: 'default', label: 'Verified' },
      pending: { variant: 'secondary', label: 'Pending Review' },
      rejected: { variant: 'destructive', label: 'Rejected' },
      expired: { variant: 'destructive', label: 'Expired' },
      expiring_soon: { variant: 'outline', label: 'Expiring Soon' },
      missing: { variant: 'outline', label: 'Missing' },
    };

    const config = variants[status] || variants.missing;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'border-l-green-500';
      case 'pending':
        return 'border-l-yellow-500';
      case 'rejected':
      case 'expired':
        return 'border-l-red-500';
      case 'expiring_soon':
        return 'border-l-orange-500';
      default:
        return 'border-l-gray-300';
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-20 bg-muted animate-pulse rounded" />
          <div className="h-20 bg-muted animate-pulse rounded" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Documents & Prequalification</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {stats.completionPercentage}% complete ({stats.verified} of {requiredDocuments.length} verified)
            </p>
          </div>
          <Button onClick={() => handleUpload()}>
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <Progress value={stats.completionPercentage} className="h-2" />
        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            {stats.missing > 0 && <span>{stats.missing} missing</span>}
            {stats.pending > 0 && <span>{stats.pending} pending</span>}
            {stats.rejected > 0 && <span>{stats.rejected} rejected</span>}
            {stats.expired > 0 && <span>{stats.expired} expired</span>}
            {stats.expiring > 0 && <span>{stats.expiring} expiring soon</span>}
            {stats.isPrequalified && (
              <Badge className="ml-auto">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Prequalified
              </Badge>
            )}
          </div>
        </div>

        {/* Document List */}
        <div className="space-y-3">
          {unifiedDocuments.map((doc, index) => (
            <div
              key={`${doc.documentType.id}-${index}`}
              className={`border-l-4 ${getStatusBorderColor(doc.status)} bg-card border rounded-lg p-4 hover:shadow-sm transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-1">
                    {getStatusIcon(doc.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{doc.documentType.name}</h4>
                      {doc.documentType.required_for_prequalification && (
                        <Badge variant="secondary" className="text-xs">Required</Badge>
                      )}
                      {getStatusBadge(doc.status)}
                    </div>

                    {doc.uploadedDocument ? (
                      <div className="space-y-2 mt-2">
                        <div className="text-sm text-muted-foreground">
                          <span className="font-medium">{doc.uploadedDocument.file_name}</span>
                          <span className="mx-2">•</span>
                          <span>{formatFileSize(doc.uploadedDocument.file_size_bytes)}</span>
                        </div>

                        {doc.uploadedDocument.document_number && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Number:</span>{' '}
                            <span className="font-mono">{doc.uploadedDocument.document_number}</span>
                          </div>
                        )}

                        {doc.uploadedDocument.expiry_date && (
                          <div className="text-sm">
                            <span className="text-muted-foreground">Expires:</span>{' '}
                            <span className={doc.status === 'expiring_soon' || doc.status === 'expired' ? 'text-red-600 font-medium' : ''}>
                              {format(new Date(doc.uploadedDocument.expiry_date), 'MMM d, yyyy')}
                            </span>
                            {doc.expiryWarning && (
                              <span className="ml-2 text-orange-600">({doc.expiryWarning})</span>
                            )}
                          </div>
                        )}

                        {doc.status === 'rejected' && doc.uploadedDocument.verification_notes && (
                          <div className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                            <span className="font-medium">Rejection reason:</span> {doc.uploadedDocument.verification_notes}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground mt-2">
                        No file uploaded
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {doc.uploadedDocument ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownload(doc.uploadedDocument)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {isTenantAdmin && doc.status === 'pending' && (
                            <DropdownMenuItem onClick={() => handleVerify(doc.uploadedDocument)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Verify Document
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleUpload(doc.documentType.name)}>
                            <Upload className="h-4 w-4 mr-2" />
                            Re-upload
                          </DropdownMenuItem>
                          {(isTenantAdmin || isContractorView) && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteClick(doc.uploadedDocument)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpload(doc.documentType.name)}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {unifiedDocuments.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No documents are currently required for prequalification.</p>
            </div>
          )}
        </div>
      </Card>

      <DocumentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        companyId={companyId}
        preselectedDocType={preselectedDocType}
      />

      {selectedDoc && (
        <DocumentVerificationDialog
          open={verifyDialogOpen}
          onOpenChange={setVerifyDialogOpen}
          documentId={selectedDoc.id}
          companyId={companyId}
          document={selectedDoc}
          onVerify={async (params) => {
            await verifyDocument({
              documentId: params.documentId,
              status: params.status,
              notes: params.notes,
              verifiedData: params.verifiedData,
              manualVerifications: params.manualVerifications,
            });
            setVerifyDialogOpen(false);
            setSelectedDoc(null);
          }}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
